// Effective vs total duration for one phase (diseño or obra) of a project.
//
// Replaces the old taskSpanDays(), which only looked at the first inicio and
// the last fin and therefore couldn't tell "worked for 40 days straight" apart
// from "worked 5 days, waited a month, worked 5 more". It also double counted
// nothing — but it hid every gap.
//
// Here the phase's tasks are merged into continuous blocks of work:
//   · efectivo = working days actually inside a block (overlaps counted once)
//   · total    = working days from the first day to the last, gaps included
//   · muertos  = total - efectivo, i.e. the waiting time between blocks
//
// Two tasks belong to the same block when the second starts on or before the
// next WORKING day after the block ends — so a task ending Friday and the next
// starting Monday is continuous work, not a two-day gap.

import { daysBetween, FERIADOS_PERU } from './workingDays';

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function toLocal(s) { return new Date(s + 'T00:00:00'); }

function fmt(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isWorkingDay(d) {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false;
  return !FERIADOS_PERU.has(fmt(d));
}

// First working day strictly after `ymd`. Friday → Monday (or later, if Monday
// is a holiday). The guard stops a runaway loop on absurd holiday data.
export function nextWorkingDayAfter(ymd) {
  if (!YMD_RE.test(ymd)) return null;
  const d = toLocal(ymd);
  d.setDate(d.getDate() + 1);
  for (let guard = 0; guard < 60 && !isWorkingDay(d); guard++) {
    d.setDate(d.getDate() + 1);
  }
  return fmt(d);
}

function normalize(tasks) {
  return (tasks || [])
    .map(t => ({
      inicio: t && t.inicio ? String(t.inicio).slice(0, 10) : '',
      fin: t && t.fin ? String(t.fin).slice(0, 10) : '',
    }))
    // YYYY-MM-DD sorts and compares correctly as plain strings.
    .filter(t => YMD_RE.test(t.inicio) && YMD_RE.test(t.fin) && t.fin >= t.inicio)
    .sort((a, b) => (a.inicio < b.inicio ? -1 : a.inicio > b.inicio ? 1 : 0));
}

// Merge a phase's tasks into continuous blocks of work.
// Returns [{ inicio, fin, dias }] ordered chronologically.
export function mergeTaskBlocks(tasks) {
  const valid = normalize(tasks);
  if (valid.length === 0) return [];
  const blocks = [];
  let current = { inicio: valid[0].inicio, fin: valid[0].fin };
  for (let i = 1; i < valid.length; i++) {
    const t = valid[i];
    const adjacency = nextWorkingDayAfter(current.fin);
    if (adjacency && t.inicio <= adjacency) {
      // Overlapping or adjacent — same block, never counted twice.
      if (t.fin > current.fin) current.fin = t.fin;
    } else {
      blocks.push(current);
      current = { inicio: t.inicio, fin: t.fin };
    }
  }
  blocks.push(current);
  return blocks.map(b => ({ ...b, dias: Number(daysBetween(b.inicio, b.fin)) || 0 }));
}

// { efectivo, total, muertos, bloques } for one phase's task list.
// Every count is in working days (weekends and FERIADOS_PERU excluded).
export function phaseMetrics(tasks) {
  const bloques = mergeTaskBlocks(tasks);
  if (bloques.length === 0) {
    return { efectivo: 0, total: 0, muertos: 0, bloques: [] };
  }
  const efectivo = bloques.reduce((sum, b) => sum + b.dias, 0);
  const total = Number(daysBetween(bloques[0].inicio, bloques[bloques.length - 1].fin)) || 0;
  return {
    efectivo,
    total,
    muertos: Math.max(0, total - efectivo),
    bloques,
  };
}

export default phaseMetrics;
